import { EnumScreenName, EventProperties } from '@calm/evexia-analytics';
import commonMessages from '@rhg/lang/messages/common';
import { useTracking } from '@rhg/libs/analytics';
import { logger } from '@rhg/libs/logger';
import { InvitationErrorType } from '@rhg/utils/invitations';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { GeneralErrorDialog } from '../general-error-dialog';
import { registrationInvitationErrorMessages } from './messages';

/**
 * The invitation link states this dialog reports. A country mismatch is not one
 * of them: it is raised by sponsorship paths that have no invitation and is
 * handled by `CountryErrorDialog`.
 */
export type InvitationLinkErrorType = Extract<
    InvitationErrorType,
    'expired' | 'claimed'
>;

const title: Record<
    InvitationLinkErrorType,
    typeof registrationInvitationErrorMessages.invitationExpiredTitle
> = {
    expired: registrationInvitationErrorMessages.invitationExpiredTitle,
    claimed: registrationInvitationErrorMessages.invitationRedeemedTitle,
};

export interface InvitationErrorDialogProps {
    type: InvitationLinkErrorType;
    trackScreen?: EnumScreenName;
    trackProps?: EventProperties;
    onClose: () => void;
}
export function InvitationErrorDialog({
    type = 'expired',
    trackScreen,
    trackProps,
    onClose,
}: InvitationErrorDialogProps) {
    const { formatMessage } = useIntl();
    const { track } = useTracking();
    const trackInvitationError = useCallback(() => {
        track('DependentRegistrationError', {
            ...(trackProps ? trackProps : {}),
            ErrorType: type,
        }).catch((err) =>
            logger.error(
                'Failed to track DependentRegistrationError',
                {},
                err as Error
            )
        );
    }, [track, trackProps, type]);

    return (
        <GeneralErrorDialog
            onOpen={trackInvitationError}
            trackDialog="DependentRegistrationError"
            trackCloseButton="Close"
            trackScreen={trackScreen}
            trackProps={trackProps}
            title={formatMessage(title[type])}
            errorContent={formatMessage(
                registrationInvitationErrorMessages.subtitle
            )}
            onClose={onClose}
            buttons={[
                {
                    trackButton: 'Close',
                    title: formatMessage(commonMessages['common.close']),
                    variant: 'text',
                    onClick: onClose,
                },
            ]}
        />
    );
}
